/**
 * @fileoverview This file defines context retrieval strategies for AI-powered document analysis, including `allFilesContextProvider`, `inMemoryLexicalSearchContextProvider`, `embeddingContextProvider`, and `thinkAndGetContext`. It uses `ContextProvider` types and functions to extract and select relevant files for AI prompts. Key classes include `LexicalIndex` and `AgenticContextProvider`.
 */

import { serializeForLlm } from '@iterator/shared-common/src/serialize_for_llm'
import { AgenticContextProvider } from './agentic_context'
import { ContextProvider, ContextProviderFn, FolderContext } from './context_types'
import { DocumentationContextProvider } from './documentation_context'
import { fileNameContextProvider, getFilesMatchingNames } from './filename_context'
import {
  getFileData,
  getFolder,
  getTokenCount,
  logFolderAndFiles,
  serializeFilesToTexts,
  serializeFolderStructure,
} from './folder'
import { doEmbeddingIndex } from './indexing/index_embedding'
import { LexicalIndex, rerank } from './indexing/index_lexical'
import { debug, debug_block, startThinkingAnimation } from '@iterator/shared-node/util/logging'
import { generateSearchTerms } from './search_terms'
import { ApiKeys } from '@iterator/shared-node/ai/api_keys'
import { ClaudeHaikuModel } from './ai/models'
import { time } from '@iterator/shared-node/util/timing'
import { baseOfSparsePath, SparsePath } from '@iterator/shared-node/util/sparse_path'

export const ContextProviders: { [key: string]: ContextProviderFn | ContextProvider } = {
  allFiles: allFilesContextProvider,
  embedding: embeddingContextProvider,
  fileName: fileNameContextProvider,
  lexical: inMemoryLexicalSearchContextProvider,
  agentic: new AgenticContextProvider(ClaudeHaikuModel),
  documentation: new DocumentationContextProvider(),
}

export const ContextProviderApiKeys: { [key: string]: ApiKeys[] } = {
  allFiles: [],
  embedding: ['VOYAGE_API_KEY'],
  fileName: [],
  lexical: [],
  agentic: ['ANTHROPIC_API_KEY'],
  documentation: ['GOOGLE_GENERATIVE_AI_API_KEY'],
}

export async function thinkAndGetContext(
  directory: SparsePath,
  prompt?: string,
  showThinkingAnimation: boolean = false,
  contextProvider: ContextProviderFn | ContextProvider = allFilesContextProvider,
): Promise<FolderContext> {
  const stopThinking =
    showThinkingAnimation && (!('shouldThink' in contextProvider) || contextProvider.shouldThink())
      ? startThinkingAnimation('Building context')
      : () => {}
  try {
    if ('getContext' in contextProvider) {
      return await contextProvider.getContext(directory, prompt)
    } else {
      return await contextProvider(directory, prompt)
    }
  } finally {
    stopThinking()
  }
}

export async function allFilesContextProvider(directory: SparsePath): Promise<FolderContext> {
  const folder = getFolder(directory)
  logFolderAndFiles(folder)

  const serializedFolder = serializeFolderStructure(folder)
  const texts = serializeFilesToTexts(folder)

  return {
    system: serializedFolder,
    texts,
  }
}

export async function inMemoryLexicalSearchContextProvider(
  directory: SparsePath,
  prompt?: string,
): Promise<FolderContext> {
  if (!prompt) {
    throw new Error('Prompt is required for lexical context provider')
  }
  const index = await new LexicalIndex('lexical').build(baseOfSparsePath(directory))
  const matchingFilePaths = await index.search(prompt)
  const fileDatas = matchingFilePaths.map((filePath) => getFileData(filePath, true))

  const system = fileDatas.map((file) => file.path).join('\n')
  const texts = fileDatas.map((file) => serializeForLlm.file(file))

  debug_block('Search context provider files', system)

  return {
    system,
    texts,
  }
}

export async function searchTermsContextProvider(
  prompt: string,
  directory: string,
): Promise<FolderContext> {
  const {
    searchTerms,
    index,
    timing: generateSearchTermsAndBuildIndex,
  } = await time('setup', async () => {
    const searchTermsPromise = generateSearchTerms(prompt)
    const buildIndexPromise = new LexicalIndex('lexical').build(directory)
    const [searchTerms, index] = await Promise.all([searchTermsPromise, buildIndexPromise])
    return { searchTerms, index }
  })

  const { matchingFilePaths, timing: searchTiming } = await time('search', async () => {
    const query = searchTerms.keyWords.concat(searchTerms.fileNames).join(' ')
    debug('Search query: ', query)
    return {
      matchingFilePaths: await index.search(query),
    }
  })
  debug(`Found ${matchingFilePaths.length} matching file paths`)

  const { fileDatas, timing: processingTiming } = await time('processing', async () => {
    return {
      fileDatas: matchingFilePaths.map((filePath) => getFileData(filePath, true)),
    }
  })
  debug('Loaded files')

  const rerankedFileDatas = rerank(prompt, fileDatas)
  debug_block(
    'Reranked files. Top 100',
    rerankedFileDatas.slice(0, 100).map((file) => file.path),
  )

  // Take the top files up to 50k tokens to stay within context limits
  let totalTokens = 0
  const { filteredFileDatas, timing: filterTiming } = await time('filtering', async () => {
    return {
      filteredFileDatas: rerankedFileDatas.filter((file) => {
        // Add slightly more than 50k tokens so we don't need to tokenize every file.
        if (totalTokens > 50000) {
          return false
        }
        const fileTokenCount = getTokenCount(file.contents)
        if (totalTokens + fileTokenCount > 50000) {
          return false
        }

        totalTokens += fileTokenCount
        debug(`Adding file ${file.path} with ${fileTokenCount} tokens`)
        return true
      }),
    }
  })

  debug_block('Files in context', filteredFileDatas.map((file) => file.path).join('\n'))
  debug_block('Timing', [
    generateSearchTermsAndBuildIndex,
    searchTiming,
    processingTiming,
    filterTiming,
  ])

  return {
    system: filteredFileDatas.map((file) => file.path).join('\n'),
    // Reverse to get most relevant files at the bottom so they are read last by the model
    texts: filteredFileDatas.map((file) => serializeForLlm.file(file)).reverse(),
  }
}

export async function embeddingContextProvider(
  directory: SparsePath,
  prompt?: string,
): Promise<FolderContext> {
  const { paths } = await doEmbeddingIndex(directory, prompt, 'voyage')
  if (!paths) {
    throw new Error('No paths found')
  }
  if (!prompt) {
    throw new Error('Prompt is required for embedding context provider')
  }

  let { allPaths, matching: nameMatches } = getFilesMatchingNames(prompt, directory)
  debug_block('Name matches', nameMatches)
  nameMatches = nameMatches.filter((path) => !paths.includes(path))
  debug_block('All paths', allPaths)

  const filesForContext = paths.concat(nameMatches)
  return {
    system: filesForContext.join('\n'),
    texts: filesForContext.map((path) => getFileData(path, true).contents),
  }
}
